import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { SpotlightCard, SpotlightPill } from "./SpotlightCard";
import { TARGET_STATUS } from "./useSpotlightTargetRect";
import { computeCardPosition, getBlockerRects, getHoleRect, isCompactViewport } from "./spotlightLayout";
import {
	SPOTLIGHT_COLORS,
	SPOTLIGHT_HOLE_PADDING,
	SPOTLIGHT_TRANSITION_MS,
	SPOTLIGHT_VIEWPORT_MARGIN,
	SPOTLIGHT_Z_INDEX,
	ensureSpotlightStyles,
} from "./spotlightTheme";

const CARD_MAX_WIDTH = 380;
const STEP_TRANSITION_WINDOW_MS = 700;
const GEOMETRY_PROPERTIES = ["left", "top", "width", "height"];

const readViewport = () => ({ width: window.innerWidth, height: window.innerHeight });

const useViewport = () => {
	const [viewport, setViewport] = useState(readViewport);
	useEffect(() => {
		const handleResize = () => setViewport(readViewport());
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);
	return viewport;
};

/** True for a short window after `stepKey` changes, so hole/card glide to a new target but track scrolling instantly. */
const useStepTransitionWindow = (stepKey) => {
	const [isTransitioning, setIsTransitioning] = useState(true);
	useEffect(() => {
		setIsTransitioning(true);
		const timeout = setTimeout(() => setIsTransitioning(false), STEP_TRANSITION_WINDOW_MS);
		return () => clearTimeout(timeout);
	}, [stepKey]);
	return isTransitioning;
};

const useMeasuredSize = (ref, dependencyKey) => {
	const [size, setSize] = useState({ width: 0, height: 0 });
	useLayoutEffect(() => {
		const element = ref.current;
		if (!element) return;
		const next = { width: element.offsetWidth, height: element.offsetHeight };
		setSize((previous) => (previous.width === next.width && previous.height === next.height ? previous : next));
	}, [dependencyKey]);
	return size;
};

const toTransition = (properties, isEnabled) =>
	isEnabled ? properties.map((property) => `${property} ${SPOTLIGHT_TRANSITION_MS}ms ease`).join(", ") : "none";

const fixedBox = (box, transition) => ({
	position: "fixed",
	left: box.left,
	top: box.top,
	width: box.width,
	height: box.height,
	transition,
});

/**
 * Renders the dimmed page with a cut-out around the target, the highlight ring, and the tutorial card.
 * The cut-out is made of four blocker panels, so the page underneath is never modified and the
 * highlighted element stays natively clickable when the step is interactive.
 */
const SpotlightOverlay = ({ step, targetState, presentation, isPreview, cardProps, pillProps }) => {
	ensureSpotlightStyles();
	const viewport = useViewport();
	const isTransitioning = useStepTransitionWindow(step.id);
	const lastHoleRef = useRef(null);
	const cardRef = useRef(null);

	const isFound = targetState.status === TARGET_STATUS.FOUND && Boolean(targetState.rect);
	const isSearching = targetState.status === TARGET_STATUS.SEARCHING;
	const isTargetMissing = targetState.status === TARGET_STATUS.MISSING || targetState.status === TARGET_STATUS.GONE;

	if (isFound) lastHoleRef.current = getHoleRect(targetState.rect, SPOTLIGHT_HOLE_PADDING, viewport);
	else if (!isSearching) lastHoleRef.current = null;
	const hole = lastHoleRef.current;

	const centerPoint = { left: viewport.width / 2, right: viewport.width / 2, top: viewport.height / 2, bottom: viewport.height / 2, width: 0, height: 0 };
	const displayedHole = hole || centerPoint;
	const isHoleClickable = isFound && Boolean(step.interactive);

	const geometryTransition = toTransition(GEOMETRY_PROPERTIES, isTransitioning);
	const showsCard = presentation === "card";
	const cardWidth = isCompactViewport(viewport) ? viewport.width - SPOTLIGHT_VIEWPORT_MARGIN * 2 : CARD_MAX_WIDTH;
	const cardSize = useMeasuredSize(cardRef, `${step.id}|${presentation}|${isTargetMissing}|${viewport.width}|${cardProps.hint || ""}`);
	const cardPosition = computeCardPosition({ hole, card: cardSize, viewport, placement: step.placement });

	const cardWrapperStyle = {
		position: "fixed",
		left: cardPosition.left,
		top: cardPosition.top,
		width: showsCard ? cardWidth : "max-content",
		maxWidth: viewport.width - SPOTLIGHT_VIEWPORT_MARGIN * 2,
		pointerEvents: "auto",
		opacity: cardSize.width > 0 ? 1 : 0,
		transition: isTransitioning ? `${toTransition(["left", "top"], true)}, opacity 200ms ease` : "opacity 200ms ease",
	};

	return (
		<div style={{ position: "fixed", inset: 0, zIndex: SPOTLIGHT_Z_INDEX, pointerEvents: "none" }} data-spotlight-root>
			{getBlockerRects(displayedHole, viewport).map(({ key, ...box }) => (
				<div key={key} style={{ ...fixedBox(box, geometryTransition), backgroundColor: SPOTLIGHT_COLORS.scrim, pointerEvents: "auto" }} />
			))}

			{hole && (
				<>
					<div
						data-spotlight-animated
						style={{
							...fixedBox(displayedHole, geometryTransition),
							borderRadius: 16,
							pointerEvents: "none",
							boxShadow: `0 0 0 3px ${SPOTLIGHT_COLORS.cream}, 0 0 0 7px rgba(76, 149, 108, 0.55)`,
							animation: step.interactive ? "spotlightRingPulse 2s ease-in-out infinite" : "none",
						}}
					/>
					{!isHoleClickable && <div style={{ ...fixedBox(displayedHole, geometryTransition), pointerEvents: "auto" }} />}
				</>
			)}

			<div ref={cardRef} style={cardWrapperStyle}>
				{showsCard ? <SpotlightCard {...cardProps} /> : <SpotlightPill {...pillProps} isPreview={isPreview} />}
			</div>
		</div>
	);
};

export default SpotlightOverlay;
